import { useQuery } from '@apollo/client';
import { useSetAtom } from 'jotai';
import React, { lazy, Suspense } from 'react';
import Button from '../components/Button';
import RightPanel from '../components/RightPanel';
import { gql } from '../graphql/generated/gql';
import { LayoutSize } from '../utils/responsive/Sizes';
import useIsLayoutSize from '../utils/responsive/useIsLayoutSize';
import {
  isBacklinksMenuOpenAtom,
  isInsightsMenuOpenAtom,
} from './controlBarMenuAtoms';
import styles from './PostInsights.module.scss';
import AvatarStack from '../components/AvatarStack';
import scrollPostToTop from './scrollPostToTop';

const PostInsightsMetrics = lazy(
  () =>
    import(
      /* webpackChunkName: "postMetrics" */
      './PostInsightsMetrics'
    ),
);

const POST_INSIGHTS_QUERY = gql(`
  query postInsights($id: ID!) {
    post(id: $id) {
      id
      backlinks {
        __typename
      }
      viewers {
        id
        ...AvatarStack_users
      }
    }
  }
`);

interface PostInsightsProps {
  close: () => void;
  id: string;
}

const PostInsights = ({ close, id }: PostInsightsProps) => {
  const setIsInsightsMenuOpen = useSetAtom(isInsightsMenuOpenAtom);
  const setIsBacklinksMenuOpen = useSetAtom(isBacklinksMenuOpenAtom);
  const isMobile = useIsLayoutSize(LayoutSize.mobile);

  const { data } = useQuery(POST_INSIGHTS_QUERY, { variables: { id } });

  const openInsightsMenu = () => {
    close();
    setIsInsightsMenuOpen(true);
    scrollPostToTop();
  };

  const openBacklinksMenu = () => {
    close();
    setIsBacklinksMenuOpen(true);
    scrollPostToTop();
  };

  return (
    <RightPanel header="Post insights" close={close}>
      {data ? (
        <div className={styles.container}>
          <div>
            <Suspense fallback={null}>
              <PostInsightsMetrics id={id} />
            </Suspense>
          </div>
          <div className={styles.readersSection}>
            <div className={styles.sectionHeader}>readers</div>
            <AvatarStack
              users={data.post.viewers}
              limit={5}
              link
              tooltip="View readers"
              expandable
            />
          </div>

          {!isMobile && (
            <div className={styles.uiUpdateNotice}>
              <div>
                We reorganized some features to dedicate this panel to just
                insights.
              </div>
              <br />
              {data.post.backlinks && data.post.backlinks.length > 0 ? (
                <div>
                  Access{' '}
                  <Button
                    onClick={openInsightsMenu}
                    className={styles.controlBarMenuButton}
                  >
                    post details here
                  </Button>{' '}
                  and{' '}
                  <Button
                    onClick={openBacklinksMenu}
                    className={styles.controlBarMenuButton}
                  >
                    backlinks here
                  </Button>
                  .
                </div>
              ) : (
                <div>
                  Access{' '}
                  <Button
                    className={styles.controlBarMenuButton}
                    onClick={openInsightsMenu}
                  >
                    post details here
                  </Button>
                  .
                </div>
              )}
            </div>
          )}
        </div>
      ) : undefined}
    </RightPanel>
  );
};

export default PostInsights;
